import * as tf from "@tensorflow/tfjs-core";
import "@tensorflow/tfjs-backend-wasm";
import "@tensorflow/tfjs-backend-webgl";

/* ================== CONSTANTS ================== */

const CLASS_LABELS = [
  "",
  "front_bumper",
  "front_door",
  "back_door",
  "front_glass",
  "fender",
  "qtr_panel",
  "window_glass",
  "running_board",
  "side_view_mirror",
  "headlight",
  "wheel",
  "back_bumper",
  "dicky",
  "back_glass",
  "taillight",
  "hood",
];

// Optimized threshold lookup: Pre-calculate the final ID-to-threshold mapping
const CLASS_THRESHOLDS = new Float32Array(17).fill(0.7);
const customThresholds = {
  8: 0.3,
  5: 0.4,
  6: 0.4,
  2: 0.6,
  7: 0.4,
  11: 0.45,
  14: 0.4,
};
Object.entries(customThresholds).forEach(
  ([idx, val]) => (CLASS_THRESHOLDS[idx] = val),
);

// Quantization Constants
const S_SCORE = 0.003807874396443367;
const S_BOX = 0.007213558070361614;
const Z_BOX = 45;
const S_CLASS = 0.05882352963089943;
const S_NUM = 0.09803921729326248;

/* ================== LOAD MODEL ================== */

export async function loadCarPartModel() {
//   const startTime = performance.now();

  // Optimized backend selection logic
//   const backend = tf.engine().registryFactory["webgl"] ? "webgl" : "wasm";
  await tf.setBackend("webgl");
  await tf.ready();

  const model = await window.tflite.loadTFLiteModel("/model_int8.tflite");

//   const loadTimeMs = performance.now() - startTime;
//   console.log(`Backend: ${backend} | Loaded in ${loadTimeMs.toFixed(2)} ms`);

  return model;
}

/* ================== PREPROCESS ================== */

export const preprocessVideo = (video) => {
  return tf.tidy(() => {
    // browser.fromPixels creates a tensor that needs cleanup via tidy
    return tf.browser
      .fromPixels(video)
      .resizeBilinear([320, 320])
      .cast("int32")
      .expandDims(0);
  });
};

/* ================== INFERENCE ================== */

export async function runCarPartDetection(model, processedVideo) {
  // Use tf.tidy for intermediate result management
  const result = model.predict(processedVideo);
  const outputs = Array.isArray(result) ? result : [result];
  const tensors = Object.values(outputs[0] || outputs);

  if (tensors.length !== 4) {
    cleanupTensors(processedVideo, outputs);
    return [];
  }

  // scoresT, boxesT, numT, classT
  const [scoresT, boxesT, numT, classT] = tensors;

  // Parallel data download from GPU/WASM memory to JS
  const [scoresData, boxesData, classData, numData] = await Promise.all([
    scoresT.data(),
    boxesT.data(),
    classT.data(),
    numT.data(),
  ]);

  const count = Math.min(Math.round(numData[0] * S_NUM), scoresData.length);
  const detections = [];

  // Single loop processing
  for (let i = 0; i < count; i++) {
    const score = scoresData[i] * S_SCORE;

    // Early exit for low scores
    if (score < 0.05) continue;

    const classId = Math.round(classData[i] * S_CLASS) + 1;
    const threshold = CLASS_THRESHOLDS[classId] || 0.7;

    if (score >= threshold && CLASS_LABELS[classId]) {
      const b = i * 4;
      const ymin = (boxesData[b] - Z_BOX) * S_BOX;
      const xmin = (boxesData[b + 1] - Z_BOX) * S_BOX;

      detections.push({
        bbox: [
          xmin,
          ymin,
          (boxesData[b + 3] - Z_BOX) * S_BOX - xmin, // width
          (boxesData[b + 2] - Z_BOX) * S_BOX - ymin, // height
        ],
        class: classId,
        label: CLASS_LABELS[classId],
        score: score,
      });
    }
  }

  cleanupTensors(processedVideo, outputs);
  return detections;
}

// Helper to ensure memory is freed
function cleanupTensors(input, outputs) {
  input.dispose();
  outputs.forEach((t) => {
    if (t && t.dispose) t.dispose();
  });
}
